import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { dumontColors } from "../../colors";
import { useAvailableAgents } from "../../hooks/useAvailableAgents";
import { getAgentTypeFromRoute, getWelcomeMessage, getAgentName } from "../../utils/routeToAgent";

interface PromptCard {
  titleKey: string;
  descKey: string;
}

const PROMPT_CARD_KEYS: PromptCard[] = [
  { titleKey: "dumontOptimizeLogisticsOperations", descKey: "dumontOptimizeLogisticsOperationsDesc" },
  { titleKey: "dumontTrackShipmentStatus", descKey: "dumontTrackShipmentStatusDesc" },
  { titleKey: "dumontCalculateShippingCosts", descKey: "dumontCalculateShippingCostsDesc" },
  { titleKey: "dumontFindWarehouseLocations", descKey: "dumontFindWarehouseLocationsDesc" },
  { titleKey: "dumontManageInventoryLevels", descKey: "dumontManageInventoryLevelsDesc" },
  { titleKey: "dumontScheduleDeliveries", descKey: "dumontScheduleDeliveriesDesc" },
];

interface FullscreenEmptyChatProps {
  onPromptClick?: (prompt: string) => void;
  selectedAgentId?: string | null;
}

export default function FullscreenEmptyChat({
  onPromptClick,
  selectedAgentId,
}: FullscreenEmptyChatProps) {
  const { t: translate } = useTranslation();
  const location = useLocation();
  const { agents: availableAgents } = useAvailableAgents();

  // Detect agent type from URL or use selected agent
  // For general routes (Dashboard, Reports, Network, Settings), return null to show default Dumont
  const currentAgentType = useMemo(() => {
    // If manually selected an agent, use that
    if (selectedAgentId) {
      const agent = availableAgents.find((a) => a.id === selectedAgentId);
      return agent?.type || null;
    }
    // Otherwise detect from URL - don't fallback to defaultAgentType for general routes
    const agentType = getAgentTypeFromRoute(location.pathname);
    return agentType;
  }, [selectedAgentId, availableAgents, location.pathname]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "24px",
        width: "100%",
        maxWidth: "1152px",
        margin: "0 auto",
      }}
    >
      {/* Prompts Section */}
      <Box sx={{ width: "100%", maxWidth: "1152px" }}>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 500,
            color: dumontColors.textTertiary,
            lineHeight: 1.4,
            marginBottom: "8px",
            paddingLeft: "8px",
            fontFamily: "Manrope",
          }}
        >
          {translate("dumontWhatCanIHelpYouWith")}
        </Typography>

        {/* Grid de Cards */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "8px",
            rowGap: "8px",
          }}
        >
          {PROMPT_CARD_KEYS.map((card, index) => (
            <Box
              key={index}
              onClick={() => onPromptClick?.(translate(card.titleKey))}
              sx={{
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
                padding: "12px 0 12px 16px",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                gap: "6px",
                height: "68.39px",
                boxSizing: "border-box",
                "&:hover": {
                  backgroundColor: "#F9FAFB",
                  borderColor: "#00A49F",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#1D2939",
                  lineHeight: 1.4,
                  fontFamily: "Manrope",
                }}
              >
                {translate(card.titleKey)}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 400,
                  color: dumontColors.textTertiary,
                  lineHeight: 1.4,
                  fontFamily: "Manrope",
                }}
              >
                {translate(card.descKey)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
